import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminScoreHistory.css";

const AdminScoreHistory = () => {
  const [groupByUser, setGroupByUser] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [scoreData, setScoreData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);

  // Fetch user data from the backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/dashboard`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.error(data.error);
          setError(data.error);
        } else {
          setUserData(data);
        }
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
        setError("Error fetching user data");
      });
  }, []);

  const empathyScoreHighThreshold = 0.6;
  const empathyScoreLowThreshold = 0.3;

  const getScoreColor = (value) => {
    if (value > empathyScoreHighThreshold * 100) return "green";
    if (value < empathyScoreLowThreshold * 100) return "red";
    return "orange";
  };

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data.message);
        navigate(`/`);
      })
      .catch((error) => console.error("Error logging out:", error));
  };

  const getFormattedDate = (date) => {
    if (!date) return "Invalid date";
    try {
      const raw = date?.$date || date;
      const parsed = new Date(raw);
      return isNaN(parsed) ? "Invalid date" : parsed.toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  useEffect(() => {
    if (!userData) return;
    fetch(
      `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/admin/score-history`,
      {
        method: "GET",
        credentials: "include",
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else {
          setScoreData(data);
          setFilteredData(data);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Unable to load score history. Please check your network.");
        setLoading(false);
      });
  }, [userData]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(scoreData);
      return;
    }

    const lower = searchTerm.toLowerCase();
    const filtered = scoreData.filter((user) =>
      user.email?.toLowerCase().includes(lower)
    );

    setFilteredData(filtered);
  }, [searchTerm, scoreData]);

  const handleToggle = () => setGroupByUser((prev) => !prev);
  const handleBackToDashboard = () => navigate("/dashboard");

  const renderScoreBreakdown = (breakdown) => {
    if (!breakdown) return <span>Invalid or missing score data</span>;
    const cats = ["Cognitive", "Affective", "Empathic Response"];
    return (
      <div>
        {cats.map((cat) => (
          <div key={cat} style={{ color: "black" }}>
            {cat}: {breakdown[cat].raw} / {breakdown[cat].max} (
            <span
              style={{
                color: getScoreColor(breakdown[cat].percent),
                fontWeight: "bold",
              }}
            >
              {breakdown[cat].percent}%
            </span>
            )
          </div>
        ))}
        <div
          style={{
            color: "black",
            marginTop: "0.75em",
          }}
        >
          <b>
            Total Normalised Score: {breakdown.total.raw} /{" "}
            {breakdown.total.max} (
            <span
              style={{
                color: getScoreColor(breakdown.total.percent),
                fontWeight: "bold",
              }}
            >
              {breakdown.total.percent}
            </span>
            )
          </b>
        </div>
      </div>
    );
  };

  if (loading) return <p className="admin-message">Loading score history...</p>;
  if (error) return <p className="admin-error">{error}</p>;

  return (
    <div className="admin-score-container">
      <div className="dash-header">
        <button onClick={handleBackToDashboard}>Dashboard</button>
        <button onClick={handleLogout}>Log Out</button>
      </div>

      <h2>User Score History</h2>

      <div className="admin-controls">
        <input
          type="text"
          placeholder="Search by name, email, or username"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={groupByUser}
            onChange={handleToggle}
          />
          Group by User
        </label>
      </div>

      <div className="admin-score-list">
        {filteredData.length === 0 ? (
          <p className="admin-message">No score history available.</p>
        ) : groupByUser ? (
          filteredData.map((user) => (
            <div className="user-group" key={user.email}>
              <h3>
                {user.email} ({user.role || "User"})
              </h3>
              {user.scores?.map((score, index) => (
                <div className="score-entry" key={index}>
                  <p>Date: {getFormattedDate(score.date)}</p>
                  <p>Quiz: {score.quiz}</p>
                  {score?.score_breakdown ? (
                    renderScoreBreakdown(score.score_breakdown)
                  ) : (
                    <p className="admin-error">Invalid or missing score data</p>
                  )}
                </div>
              ))}
            </div>
          ))
        ) : (
          filteredData
            .flatMap((user) =>
              user.scores?.map((score, index) => ({
                ...score,
                email: user.email,
                role: user.role || "User",
                key: `${user.email}-${index}`,
              }))
            )
            .sort((a, b) =>
              new Date(a.date?.$date || a.date) <
              new Date(b.date?.$date || b.date)
                ? 1
                : -1
            )
            .map((entry, idx) => (
              <div className="score-entry" key={entry.key || idx}>
                <h3>
                  User: {entry.email} ({entry.role})
                </h3>
                <p>Date: {getFormattedDate(entry.date)}</p>
                <p>Quiz: {entry.quiz}</p>
                {entry?.score_breakdown ? (
                  renderScoreBreakdown(entry.score_breakdown)
                ) : (
                  <p className="admin-error">Invalid or missing score data</p>
                )}
              </div>
            ))
        )}
      </div>
    </div>
  );
};

export default AdminScoreHistory;
